import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import { CUSTOM_FIELD_DEFINITIONS_COLLECTION } from '@/modules/inventory/services/constants';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInput,
} from '@/modules/inventory/types';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type CustomFieldDoc = {
  companyId: string;
  categoryId: string;
  subcategoryId: string | null;
  key: string;
  label: string;
  type: CustomFieldDefinition['type'];
  options: string[];
  required: boolean;
  sortOrder: number;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapCustomField(id: string, data: CustomFieldDoc): CustomFieldDefinition {
  return {
    id,
    companyId: data.companyId,
    categoryId: data.categoryId,
    subcategoryId: data.subcategoryId,
    key: data.key,
    label: data.label,
    type: data.type,
    options: data.options ?? [],
    required: data.required,
    sortOrder: data.sortOrder,
    active: data.active,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listCustomFieldDefinitions(
  companyId: string = appConfig.companyId,
  categoryId?: string,
): Promise<CustomFieldDefinition[]> {
  const q = categoryId
    ? query(
        collection(getFirestoreDb(), CUSTOM_FIELD_DEFINITIONS_COLLECTION),
        where('companyId', '==', companyId),
        where('categoryId', '==', categoryId),
      )
    : query(
        collection(getFirestoreDb(), CUSTOM_FIELD_DEFINITIONS_COLLECTION),
        where('companyId', '==', companyId),
      );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapCustomField(d.id, d.data() as CustomFieldDoc))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
}

/** Campos aplicables a un ítem (categoría + opcional subcategoría). */
export async function listCustomFieldsForItem(
  categoryId: string,
  subcategoryId: string | null,
): Promise<CustomFieldDefinition[]> {
  const all = await listCustomFieldDefinitions(appConfig.companyId, categoryId);
  return all.filter(
    (f) =>
      f.active &&
      (f.subcategoryId === null || f.subcategoryId === subcategoryId),
  );
}

export async function saveCustomFieldDefinition(
  input: CustomFieldDefinitionInput,
  id?: string,
): Promise<CustomFieldDefinition> {
  return withFirestoreError(async () => {
  const ref = id
    ? doc(getFirestoreDb(), CUSTOM_FIELD_DEFINITIONS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), CUSTOM_FIELD_DEFINITIONS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(
        mapCustomField(existing.id, existing.data() as CustomFieldDoc),
      )
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    categoryId: input.categoryId,
    subcategoryId: input.subcategoryId,
    key: input.key.trim(),
    label: input.label.trim(),
    type: input.type,
    options: input.options,
    required: input.required,
    sortOrder: input.sortOrder,
    active: input.active,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar el campo.');
  const result = mapCustomField(saved.id, saved.data() as CustomFieldDoc);
  recordEntityWrite({
    entityType: 'customFieldDefinition',
    entityId: result.id,
    isCreate: !existing.exists(),
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result),
  });
  return result;
  }, 'customField');
}
